import {
  createAsyncThunk,
  createSelector,
  createSlice,
  PayloadAction,
} from "@reduxjs/toolkit";
import {
  StoreLocationModel,
  StoreMenuModel,
  StoreGatewayModel,
  StoreModel,
  StoreCategoryModel,
  StoreProductModel,
  StoreCouponModel,
  LoyaltyLevelModel,
  OrderTypeModel,
} from "../models";
import { GetStoreLocations, GetStoreMenu } from "../services";
import { getStoreIdFromUrl, getUtmFromUrl } from "../helpers";
import { getEmbedConfig } from "../embed/config";
import { setDocumentTitle, setScopeVars } from "../embed/dom";
import { legacyOrderingUrl } from "../embed/urls";
import { OrderType } from "../enums";
import { RootState } from ".";
import { Toast } from "../components/Toast";

import { local } from "../embed/storage";
const utmTag = import.meta.env.VITE_APP_UTM_TAG;

// ---- Initial values (similar to your context) ----
const initialStoreId = getStoreIdFromUrl();
const utmFromUrl = getUtmFromUrl();

const initialUtm = (() => {
  try {
    const stored = local.getItem("olo_utm") || "";
    if (utmFromUrl) {
      // prefer UTM from URL and store it
      local.setItem("olo_utm", utmFromUrl);
      // Remove the fragment from the URL now that we have stored the tag.
      // Skipped in hash-router mode, where the fragment IS the route.
      if (getEmbedConfig().routerMode !== "hash") {
        window.history.replaceState(
          null,
          "",
          window.location.pathname + window.location.search,
        );
      }
      return utmFromUrl;
    }
    return stored;
  } catch {
    return utmFromUrl || "";
  }
})();

export interface StoreState {
  storeId: string;
  menuId: string;
  utm: string;
  isMenuLoading: boolean;
  isLocationLoading: boolean;
  giftcardMenu?: StoreMenuModel;
  storeMenu?: StoreMenuModel;
  storeCategories: StoreCategoryModel[];
  storeProducts: StoreProductModel[];
  storeCoupons: StoreCouponModel[];
  storeLocations?: StoreLocationModel;
  selectedStore?: StoreModel;
  storeGateways: StoreGatewayModel[];
  isLoyaltyActive: boolean;
  loyaltyLevels: LoyaltyLevelModel[];
  isGiftcardActive: boolean;
}

const initialState: StoreState = {
  storeId: initialStoreId,
  menuId: "",
  utm: initialUtm,
  isMenuLoading: false,
  isLocationLoading: false,
  giftcardMenu: undefined,
  storeMenu: undefined,
  storeCategories: [],
  storeProducts: [],
  storeCoupons: [],
  storeLocations: undefined,
  selectedStore: undefined,
  storeGateways: [],
  isLoyaltyActive: false,
  loyaltyLevels: [],
  isGiftcardActive: false,
};

// ---- Thunks (menu + locations) ----
export const fetchStoreMenu = createAsyncThunk<
  StoreMenuModel,
  string,
  { state: RootState; rejectValue: string }
>(
  "store/fetchMenu",
  async (storeId, { rejectWithValue }) => {
    const { status, message, data } = await GetStoreMenu({ store_id: storeId });

    if (!status) {
      Toast.error(message);
      return rejectWithValue(message);
    }

    return data;
  },
  {
    condition: (_, { getState }) => {
      const { store } = getState();

      // already loading or already loaded → skip
      if (store.isMenuLoading) return false;
      if (store.storeMenu) return false;

      return true;
    },
  },
);

export const fetchStoreLocations = createAsyncThunk<
  { data: StoreLocationModel; storeId: string },
  string,
  { state: RootState; rejectValue: string }
>(
  "store/fetchLocations",
  async (storeId, { rejectWithValue }) => {
    const { status, message, data } = await GetStoreLocations({
      store_id: storeId,
    });

    if (!status) {
      Toast.error(message);
      return rejectWithValue(message);
    }

    if (data?.name) {
      setDocumentTitle(data.name);
    }

    // Embedded, brand variables are scoped to our own container rather than
    // painted onto the client page's <html> element.

    const hexToRgb = (hex: string): string => {
      // Remove the '#' if present
      hex = hex.replace(/^#/, "");

      // Convert shorthand (#FFF) to full form (#FFFFFF)
      if (hex.length === 3) {
        hex = hex
          .split("")
          .map((char) => char + char)
          .join("");
      }

      // Validate hex code
      if (!/^[0-9A-Fa-f]{6}$/.test(hex)) {
        throw new Error("Invalid hex color code");
      }

      const r = parseInt(hex.substring(0, 2), 16);
      const g = parseInt(hex.substring(2, 4), 16);
      const b = parseInt(hex.substring(4, 6), 16);

      return `${r}, ${g}, ${b}`;
    };

    const getReadableTextColor = (hex: string): string => {
      const normalizedHex = hex.replace(/^#/, "");
      const fullHex =
        normalizedHex.length === 3
          ? normalizedHex
              .split("")
              .map((char) => char + char)
              .join("")
          : normalizedHex;

      const red = parseInt(fullHex.substring(0, 2), 16) / 255;
      const green = parseInt(fullHex.substring(2, 4), 16) / 255;
      const blue = parseInt(fullHex.substring(4, 6), 16) / 255;
      const toLinear = (channel: number) =>
        channel <= 0.03928
          ? channel / 12.92
          : Math.pow((channel + 0.055) / 1.055, 2.4);
      const luminance =
        0.2126 * toLinear(red) +
        0.7152 * toLinear(green) +
        0.0722 * toLinear(blue);

      return luminance < 0.3 ? "var(--color-white)" : "var(--text-primary)";
    };

    const primaryColor = data.theme_color.replace("##", "#");
    const secondaryColor = data.theme_color_secondary.replace("##", "#");

    // Written to every scope root, not just one element: embedded, modals live
    // in a body-level portal host that is a sibling of the mount container and
    // so inherits nothing from it.
    setScopeVars({
      "--brand-primary": primaryColor,
      "--brand-secondary": secondaryColor,
      "--on-brand-primary": getReadableTextColor(primaryColor),
      "--on-brand-secondary": getReadableTextColor(secondaryColor),
      "--brand-primary-soft-rgb": hexToRgb(primaryColor),
    });

    if (data?.version === "3.0") {
      window.location.href = legacyOrderingUrl(storeId);
      return rejectWithValue("Redirecting");
    }

    return { data, storeId };
  },
  {
    condition: (_, { getState }) => {
      const { store } = getState();

      if (store.isLocationLoading) return false;
      if (store.storeLocations) return false;

      return true;
    },
  },
);

// ---- Slice ----
const storeSlice = createSlice({
  name: "store",
  initialState,
  reducers: {
    setStoreId(state, action: PayloadAction<string>) {
      state.storeId = action.payload;
    },
    setMenuId(state, action: PayloadAction<string>) {
      state.menuId = action.payload;
    },
    setUtm(state, action: PayloadAction<string>) {
      state.utm = action.payload;
      try {
        local.setItem("olo_utm", action.payload);
      } catch {}
    },
    setStoreGateways(state, action: PayloadAction<StoreGatewayModel[]>) {
      state.storeGateways = action.payload;
    },
    setGiftcardMenu(state, action: PayloadAction<StoreMenuModel>) {
      state.giftcardMenu = action.payload;
    },
    applyInflation(state, action: PayloadAction<string | number>) {
      const typeId = action.payload;

      state.storeProducts = state.storeProducts.map((product) => {
        if (!product.is_inflated) return product;

        const productInflation = Array.isArray(product.convenience)
          ? 0
          : Number(product.convenience[String(typeId)] || 0);

        const updatedModifierGroups = product.modifier_group.map(
          (modifierGroup) => ({
            ...modifierGroup,
            modifier: modifierGroup.modifier.map((modifier) => {
              const modifierInflation = Array.isArray(modifier.convenience)
                ? 0
                : Number(modifier.convenience?.[String(typeId)] || 0);

              return {
                ...modifier,
                m_price: modifier.m_mprice + modifierInflation,
              };
            }),
          }),
        );

        return {
          ...product,
          product_price: product.product_mrice + productInflation,
          modifier_group: updatedModifierGroups,
        };
      });
    },
  },
  extraReducers: (builder) => {
    // Menu
    builder
      .addCase(fetchStoreMenu.pending, (state) => {
        state.isMenuLoading = true;
      })
      .addCase(fetchStoreMenu.fulfilled, (state, action) => {
        state.isMenuLoading = false;
        state.storeMenu = action.payload;
        state.menuId = action.payload.menu_id;
        state.storeCategories = action.payload.category;
        state.storeProducts = action.payload.product;
        state.storeCoupons = action.payload.coupons;
      })
      .addCase(fetchStoreMenu.rejected, (state) => {
        state.isMenuLoading = false;
      });

    // Locations
    builder
      .addCase(fetchStoreLocations.pending, (state) => {
        state.isLocationLoading = true;
      })
      .addCase(fetchStoreLocations.fulfilled, (state, action) => {
        state.isLocationLoading = false;
        state.storeLocations = action.payload.data;
        const selected = action.payload.data.locations.find(
          (loc) => String(loc.store_id) === action.payload.storeId,
        );
        state.selectedStore = selected;
        state.isLoyaltyActive = selected?.loyalty_on === "1" || false;
        state.isGiftcardActive = selected?.giftcard_on === "1" || false;
        state.loyaltyLevels = selected?.loyalty_levels || [];
      })
      .addCase(fetchStoreLocations.rejected, (state) => {
        state.isLocationLoading = false;
      });
  },
});

export const {
  setStoreId,
  setMenuId,
  setUtm,
  setStoreGateways,
  setGiftcardMenu,
  applyInflation,
} = storeSlice.actions;

export default storeSlice.reducer;

// ---- Selectors / helpers ----
export const selectHasAdInflation = (state: { store: StoreState }) =>
  state.store.utm === utmTag;

export const selectOrderTypes = createSelector(
  (state: RootState) => state.store.selectedStore,
  (selectedStore) =>
    Array.isArray(selectedStore?.order_type) ? selectedStore.order_type : [],
);

export const selectDefaultOrderType = createSelector(
  selectOrderTypes,
  (state: RootState) => state.orderOption.value.type_id,
  (orderTypes, typeId) => {
    if (typeId) return typeId;
    const available = new Set(orderTypes.map((t: OrderTypeModel) => t.id));
    if (available.has(OrderType.PICKUP)) return OrderType.PICKUP;
    if (available.has(OrderType.DELIVERY)) return OrderType.DELIVERY;
    if (available.has(OrderType.DINEIN)) return OrderType.DINEIN;
    return OrderType.PICKUP;
  },
);
